import React, { useCallback, useEffect, useRef, useState } from "react";
import { Medal } from "lucide-react";
import { api } from "../lib/api";
import { Section, KpiCard, InsightsGrid, ChatPanel, ModuleHeader } from "../components/intel/IntelKit";
import useBusinessProfile from "../hooks/useBusinessProfile";
import { getCurrentCustomer } from "../lib/customerSession";
import ConnectTallyBanner from "../components/ConnectTallyBanner";

// ---------------------------------------------------------------------------
// SECTION 1 — KPI definitions (real-data only)
//
// 2026-11 (focused Benchmarking revamp): every removed KPI below (Rev /
// Employee, GM Rank, CCC Rank, NPS Rank, Data Sources) was a placeholder —
// none had a defined competitive scoring methodology or real peer cohort
// behind it. Only Overall Rank remains, and it stays dashed ("Not yet
// available") until a real peer cohort with ≥10 companies exists — the
// `realKey: null` below means it is NEVER backed by an invented number.
// ---------------------------------------------------------------------------
const KPIS = [
  { id: "rank", label: "Overall Rank", sub: "Not yet available — needs a real peer cohort", status: "blue", icon: Medal, realKey: null, format: "raw", tooltip: "Activates once 30 days of Tally data are synced AND a real peer cohort with ≥10 fabricators is available. We never invent ranks." },
];

function realValueForBench(realKey, customerData) {
  if (!realKey || !customerData) return null;
  switch (realKey) {
    case "gmPct":     return customerData?.financial?.gross_margin_pct ?? null;
    case "cccDays":   return customerData?.derived?.ccc_days ?? null;
    case "revPerEmp": return customerData?.derived?.revenue_per_employee ?? null;
    default:          return null;
  }
}

// ---------------------------------------------------------------------------
// SECTION 1C — Competitive Landscape (Benchmarking Phase 2, Feb 2026)
//
// Company → Industry/Peer Benchmark → Comparable Competitors → Relevant
// Industry Leaders. Reads the LATEST persisted `benchmark_snapshots` row
// on every page load (zero LLM calls) — a "Refresh comparison" click is
// the ONLY thing that triggers new research (async job, polled below,
// 30-min cooldown enforced server-side).
//
// Evidence truth hierarchy (see migrations/2026-02_benchmark_competitive_
// intelligence.sql): Your Company = internal actual (live, computed
// above, never stored as evidence). Every external row is one of
// external_sourced (named publisher/date) | external_unsourced (no named
// source — never shown as a quantitative fact) | c_ore_inference (our own
// reasoning, e.g. why a company is comparable) | unavailable (genuinely
// not found). Nothing here is independently fact-checked by C-ORE.
//
// Competitor/leader identity also carries an evidence-earned
// comparability_state: discovered → relevant → quantitatively_comparable.
// Discovery alone never implies a company is a valid quantitative
// comparable — that only happens once sourced numeric evidence backs it.
// ---------------------------------------------------------------------------
const BENCH_REFRESH_POLL_MS    = 3000;
const BENCH_REFRESH_TIMEOUT_MS = 150_000;

const REFRESH_STAGE_LABELS = {
  queued:               "Queued…",
  discovering:          "Loading your company profile…",
  loading_company_identity: "Loading your company profile…",
  researching:          "Researching comparable companies & industry benchmarks…",
  researching_web:      "Researching comparable companies & industry benchmarks…",
  extracting_evidence:  "Extracting sourced facts…",
};

const EVIDENCE_TYPE_BADGE = {
  external_sourced:   { label: "Sourced",          cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  external_unsourced: { label: "Unsourced",         cls: "border-amber-200 bg-amber-50 text-amber-700" },
  c_ore_inference:    { label: "C-ORE inference",   cls: "border-sky-200 bg-sky-50 text-sky-700" },
  unavailable:        { label: "Not found",         cls: "border-slate-200 bg-slate-100 text-slate-500" },
};

const COMPARABILITY_BADGE = {
  discovered:                { label: "Discovered",                cls: "bg-slate-100 text-slate-500" },
  relevant:                  { label: "Relevant",                   cls: "bg-sky-50 text-sky-700" },
  quantitatively_comparable: { label: "Quantitatively Comparable",  cls: "bg-emerald-50 text-emerald-700" },
};

// Product-Driven Competitor Discovery (2026-02) — `category` (WHAT a
// company is) is independent of the founder-review workflow. Legacy rows
// (pre-benchmark_profile, category='competitor'/'top_performer') render
// no badge here — identical to pre-2026-02 look, forever.
const CATEGORY_BADGE = {
  direct_competitor:  { label: "Direct Competitor",  cls: "border-rose-200 bg-rose-50 text-rose-700" },
  comparable_company: { label: "Comparable Company", cls: "border-indigo-200 bg-indigo-50 text-indigo-700" },
  industry_leader:    { label: "Industry Leader",     cls: "border-amber-200 bg-amber-50 text-amber-700" },
};

function fmtShortInr(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  const v = Number(n);
  if (Math.abs(v) >= 1_00_00_000) return `₹${(v / 1_00_00_000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 1_00_000)    return `₹${(v / 1_00_000).toFixed(1)}L`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}

function fmtCooldown(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function EvidenceRow({ e }) {
  if (e.metric === "relevance_rationale") {
    return (
      <div data-testid={`bench-evidence-${e.id}`} className="border-b border-slate-100 py-2 text-[11.5px] italic text-slate-500 last:border-b-0">
        "{e.value_text}"
      </div>
    );
  }
  const badge = EVIDENCE_TYPE_BADGE[e.evidence_type] || EVIDENCE_TYPE_BADGE.unavailable;
  const value = e.evidence_type === "unavailable"
    ? "Not found"
    : (e.value_numeric != null ? `${e.value_numeric}${e.unit ? ` ${e.unit}` : ""}` : (e.value_text || "—"));
  return (
    <div data-testid={`bench-evidence-${e.id}`} className="flex items-start justify-between gap-3 border-b border-slate-100 py-2 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-medium capitalize text-[#0F1B2D]">{e.metric.replace(/_/g, " ")}</div>
        <div className="truncate text-[11px] text-slate-500">{value}{e.period_label ? ` · ${e.period_label}` : ""}</div>
        {e.source_publisher && <div className="truncate text-[10.5px] text-slate-400">Source: {e.source_publisher}</div>}
      </div>
      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>
    </div>
  );
}

function CompanyCard({ company, testidPrefix }) {
  const rationaleRow = company.evidence.find((e) => e.metric === "relevance_rationale");
  const factRows = company.evidence.filter((e) => e.metric !== "relevance_rationale");
  const badge = COMPARABILITY_BADGE[company.comparability_state] || COMPARABILITY_BADGE.discovered;
  const categoryBadge = CATEGORY_BADGE[company.category];
  return (
    <div data-testid={`${testidPrefix}-${company.competitor_id}`} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[13px] font-semibold text-[#0F1B2D]">{company.company_name}</div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.cls}`}>{badge.label}</span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {categoryBadge && (
          <span data-testid={`${testidPrefix}-category-${company.competitor_id}`} className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${categoryBadge.cls}`}>
            {categoryBadge.label}
          </span>
        )}
        {company.discovery_method === "founder_added" && (
          <span className="text-[10.5px] text-sky-600">Added by you</span>
        )}
      </div>
      {rationaleRow && <div className="mt-1 text-[11.5px] italic text-slate-500">"{rationaleRow.value_text}"</div>}
      <div className="mt-2">
        {factRows.length > 0
          ? factRows.map((e) => <EvidenceRow key={e.id} e={e} />)
          : <div className="text-[11px] text-slate-400">No additional facts found yet.</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Benchmark Profile (2026-02, Product-Driven Competitor Discovery) — the
// founder's real Product / Market Segment / Country. Its mere existence
// switches discovery from generic industry-based to product-driven on the
// NEXT refresh (no separate "confirm" step — discovery is continuous).
// Customers who skip this keep the original industry-based discovery,
// unchanged, forever.
// ---------------------------------------------------------------------------
function BenchmarkProfileCard({ customerId, profile, onSaved }) {
  const [editing, setEditing] = useState(!profile);
  const [product, setProduct] = useState(profile?.primary_product || "");
  const [segment, setSegment] = useState(profile?.market_segment || "");
  const [country, setCountry] = useState(profile?.country || "India");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setProduct(profile?.primary_product || "");
    setSegment(profile?.market_segment || "");
    setCountry(profile?.country || "India");
    setEditing(!profile);
  }, [profile]);

  const submit = async () => {
    if (!product.trim() || !segment.trim() || !country.trim() || !customerId) return;
    setBusy(true); setErr(null);
    const res = await api.benchmarkSaveProfile(customerId, {
      primaryProduct: product.trim(), marketSegment: segment.trim(), country: country.trim(),
    });
    setBusy(false);
    if (res.ok) { setEditing(false); onSaved(res.data.profile); }
    else setErr(res.error || "Could not save your Benchmark Profile.");
  };

  return (
    <Section
      testid="bench-profile"
      title="Benchmark Profile"
      subtitle="Your real Product, Market Segment & Country — the primary context for competitor discovery (industry is only a secondary signal)"
    >
      {!editing && profile ? (
        <div data-testid="bench-profile-summary" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex flex-wrap gap-4 text-[12.5px] text-[#0F1B2D]">
            <span><span className="text-slate-400">Product:</span> {profile.primary_product}</span>
            <span><span className="text-slate-400">Segment:</span> {profile.market_segment}</span>
            <span><span className="text-slate-400">Country:</span> {profile.country}</span>
          </div>
          <button
            type="button" data-testid="bench-profile-edit-btn" onClick={() => setEditing(true)}
            className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11.5px] font-medium hover:bg-slate-50"
          >
            Edit
          </button>
        </div>
      ) : (
        <div data-testid="bench-profile-form" className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
          <div data-testid="bench-profile-empty" className="mb-2 text-[12px] text-slate-500">
            {profile
              ? "Update your product, target market segment, and country."
              : "Not set yet — competitor discovery is currently using generic industry-based discovery. Set this to switch to targeted product-driven discovery."}
          </div>
          <div className="grid gap-2 md:grid-cols-3">
            <input
              data-testid="bench-profile-product-input" value={product} onChange={(e) => setProduct(e.target.value)}
              placeholder="Product, e.g. Robot Vacuum Cleaner"
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none"
            />
            <input
              data-testid="bench-profile-segment-input" value={segment} onChange={(e) => setSegment(e.target.value)}
              placeholder="Market Segment, e.g. Premium Consumer"
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none"
            />
            <input
              data-testid="bench-profile-country-input" value={country} onChange={(e) => setCountry(e.target.value)}
              placeholder="Country, e.g. India"
              className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none"
            />
          </div>
          {err && <div data-testid="bench-profile-error" className="mt-2 text-[11.5px] text-rose-600">{err}</div>}
          <div className="mt-2 flex gap-2">
            <button
              type="button" data-testid="bench-profile-save-btn" onClick={submit}
              disabled={busy || !product.trim() || !segment.trim() || !country.trim()}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save Benchmark Profile"}
            </button>
            {profile && (
              <button
                type="button" data-testid="bench-profile-cancel-btn" onClick={() => setEditing(false)}
                className="rounded-md px-3 py-1.5 text-[12px] text-slate-500 hover:bg-slate-100"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Multi-Product Benchmark Profiles (2026-11) — up to 10 active products per
// company, each with its own competitor-intelligence scope. Tries the new
// /benchmark/products endpoints first; if the DB migration
// (migrations/2026-11_benchmark_multi_product.sql) hasn't been applied yet,
// falls back to the exact pre-existing single-profile `BenchmarkProfileCard`
// above — zero regression for customers using today's single-product flow.
// ---------------------------------------------------------------------------
const EMPTY_PRODUCT = {
  product_name: "", category: "", description: "", specifications: "",
  market_segment: "", target_customer: "", country: "India", price_range: "", product_url: "",
};
const MAX_PRODUCTS = 10;

function ProductForm({ form, setForm, busy, err, onSave, onCancel, saveLabel }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div data-testid="bench-product-form" className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3">
      <div className="grid gap-2 md:grid-cols-3">
        <input data-testid="bench-product-name-input" value={form.product_name} onChange={set("product_name")}
          placeholder="Product name, e.g. Robot Vacuum Cleaner"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
        <input data-testid="bench-product-category-input" value={form.category} onChange={set("category")}
          placeholder="Category, e.g. Consumer Electronics"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
        <input data-testid="bench-product-segment-input" value={form.market_segment} onChange={set("market_segment")}
          placeholder="Market segment, e.g. Premium Consumer"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
        <input data-testid="bench-product-customer-input" value={form.target_customer} onChange={set("target_customer")}
          placeholder="Target customer, e.g. Urban homeowners 30-50"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
        <input data-testid="bench-product-country-input" value={form.country} onChange={set("country")}
          placeholder="Geography, e.g. India"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
        <input data-testid="bench-product-price-input" value={form.price_range} onChange={set("price_range")}
          placeholder="Price range, e.g. ₹8,000 - ₹15,000"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
        <input data-testid="bench-product-url-input" value={form.product_url} onChange={set("product_url")}
          placeholder="Product URL (optional)"
          className="rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none md:col-span-2" />
      </div>
      <textarea data-testid="bench-product-description-input" value={form.description} onChange={set("description")}
        placeholder="Description / specifications — the more specific, the better the competitor discovery"
        rows={2}
        className="mt-2 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none" />
      {err && <div data-testid="bench-product-form-error" className="mt-2 text-[11.5px] text-rose-600">{err}</div>}
      <div className="mt-2 flex gap-2">
        <button type="button" data-testid="bench-product-save-btn" onClick={onSave}
          disabled={busy || !form.product_name.trim() || !form.market_segment.trim() || !form.country.trim()}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium disabled:cursor-not-allowed disabled:opacity-50">
          {busy ? "Saving…" : saveLabel}
        </button>
        <button type="button" data-testid="bench-product-cancel-btn" onClick={onCancel}
          className="rounded-md px-3 py-1.5 text-[12px] text-slate-500 hover:bg-slate-100">
          Cancel
        </button>
      </div>
    </div>
  );
}

function ProductRow({ product, selected, onSelect, onEdit, onRemove, busy }) {
  return (
    <div
      data-testid={`bench-product-row-${product.id}`}
      onClick={() => onSelect(product.id)}
      className={`flex cursor-pointer flex-wrap items-center justify-between gap-2 rounded-lg border p-3 transition-colors ${
        selected ? "border-sky-300 bg-sky-50" : "border-slate-200 bg-white hover:bg-slate-50"
      }`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-semibold text-[#0F1B2D]">{product.product_name}</span>
          {product.is_primary && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">Primary</span>}
        </div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11px] text-slate-500">
          {product.category && <span>{product.category}</span>}
          {product.market_segment && <span>{product.market_segment}</span>}
          {product.country && <span>{product.country}</span>}
        </div>
      </div>
      <div className="flex shrink-0 gap-2" onClick={(e) => e.stopPropagation()}>
        <button type="button" data-testid={`bench-product-edit-${product.id}`} onClick={() => onEdit(product)}
          className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11px] font-medium hover:bg-slate-50">
          Edit
        </button>
        <button type="button" data-testid={`bench-product-remove-${product.id}`} disabled={busy} onClick={() => onRemove(product.id)}
          className="rounded-full border border-rose-200 bg-white px-3 py-1 text-[11px] font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50">
          Remove
        </button>
      </div>
    </div>
  );
}

function BenchmarkProductsManager({ customerId, selectedProductId, onSelectProduct, onProductsChanged }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    if (!customerId) { setLoading(false); return; }
    const res = await api.benchmarkListProducts(customerId);
    // The DB column is `primary_product`; the UI/API write contract uses
    // `product_name`. Normalise so rows + the edit form show the real name.
    if (res.ok) setProducts((res.data.products || []).map((p) => ({ ...p, product_name: p.product_name ?? p.primary_product ?? "" })));
    setLoading(false);
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (products.length && !selectedProductId) {
      onSelectProduct((products.find((p) => p.is_primary) || products[0]).id);
    }
  }, [products, selectedProductId, onSelectProduct]);

  const startAdd = () => { setForm(EMPTY_PRODUCT); setEditingId(null); setErr(null); setFormOpen(true); };
  const startEdit = (p) => {
    // Null DB columns must not override the "" defaults (null `value` props +
    // `.trim()` on null in the Save-disabled check would crash the form).
    const filled = Object.fromEntries(Object.keys(EMPTY_PRODUCT).map((k) => [k, p[k] ?? ""]));
    setForm(filled); setEditingId(p.id); setErr(null); setFormOpen(true);
  };
  const cancelForm = () => { setFormOpen(false); setEditingId(null); setErr(null); };

  const submit = async () => {
    setBusy(true); setErr(null);
    const body = { ...form, product_name: form.product_name.trim() };
    const res = editingId
      ? await api.benchmarkUpdateProduct(customerId, editingId, body)
      : await api.benchmarkAddProduct(customerId, body);
    setBusy(false);
    if (res.ok) {
      setFormOpen(false); setEditingId(null);
      await load();
      onProductsChanged?.();
    } else setErr(res.error || "Could not save this product.");
  };

  const remove = async (id) => {
    setBusy(true); setErr(null);
    const res = await api.benchmarkRemoveProduct(customerId, id);
    setBusy(false);
    if (res.ok) {
      // Removing the SELECTED product: resetting the selection re-fetches the
      // snapshot for the fallback product. Calling onProductsChanged here too
      // would fire the stale loadSnapshot closure (still scoped to the deleted
      // id) LAST, and the request-sequence guard would keep that stale result.
      const wasSelected = selectedProductId === id;
      // Drop it locally first so the auto-select effect can't re-pick the
      // just-deleted (possibly primary) row from the stale list.
      setProducts((prev) => prev.filter((p) => p.id !== id));
      if (wasSelected) onSelectProduct(null);
      await load();
      if (!wasSelected) onProductsChanged?.();
    } else setErr(res.error || "Could not remove this product.");
  };

  const atLimit = products.length >= MAX_PRODUCTS;

  return (
    <Section
      testid="bench-products"
      title="Benchmark Profiles"
      subtitle="Up to 10 products/company — each gets its own competitor discovery and intelligence"
    >
      {loading ? (
        <div className="text-[12px] text-slate-400">Loading…</div>
      ) : (
        <>
          <div className="mb-2 flex items-center justify-between">
            <span data-testid="bench-products-count" className="text-[11.5px] text-slate-500">
              {products.length} of {MAX_PRODUCTS} products
            </span>
            <button type="button" data-testid="bench-product-add-btn" onClick={startAdd} disabled={atLimit}
              className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11.5px] font-medium hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">
              {atLimit ? "Limit reached (10)" : "+ Add Product"}
            </button>
          </div>

          {products.length === 0 && !formOpen && (
            <div data-testid="bench-products-empty" className="mb-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-[12px] text-slate-500">
              No products yet — add your first product so competitor discovery can be targeted to it, rather than generic industry-based discovery.
            </div>
          )}

          {products.length > 0 && (
            <div className="mb-2 space-y-2">
              {products.map((p) => (
                <ProductRow
                  key={p.id} product={p} selected={selectedProductId === p.id}
                  onSelect={onSelectProduct} onEdit={startEdit} onRemove={remove} busy={busy}
                />
              ))}
            </div>
          )}

          {formOpen && (
            <ProductForm
              form={form} setForm={setForm} busy={busy} err={err} onSave={submit} onCancel={cancelForm}
              saveLabel={editingId ? "Save changes" : "Add product"}
            />
          )}
        </>
      )}
    </Section>
  );
}

function BenchmarkProductsSection({ customerId, selectedProductId, onSelectProduct, onProductsChanged }) {
  const [multiAvailable, setMultiAvailable] = useState(null); // null = checking
  const [legacyProfile, setLegacyProfile] = useState(null);

  useEffect(() => {
    if (!customerId) return;
    (async () => {
      const res = await api.benchmarkListProducts(customerId);
      if (res.ok) { setMultiAvailable(true); return; }
      // Load the legacy profile BEFORE switching to the fallback card, so an
      // existing profile never flashes as an empty "Not set yet" form.
      const legacy = await api.benchmarkGetProfile(customerId);
      if (legacy.ok) setLegacyProfile(legacy.data.profile || null);
      setMultiAvailable(false);
    })();
  }, [customerId]);

  if (multiAvailable === null) return null;
  if (multiAvailable) {
    return (
      <BenchmarkProductsManager
        customerId={customerId} selectedProductId={selectedProductId}
        onSelectProduct={onSelectProduct} onProductsChanged={onProductsChanged}
      />
    );
  }
  return (
    <BenchmarkProfileCard
      customerId={customerId} profile={legacyProfile}
      onSaved={(saved) => { if (saved) setLegacyProfile(saved); onProductsChanged?.(); }}
    />
  );
}


function SuggestedCard({ company, onReview, busyId }) {
  const rationaleRow = company.evidence.find((e) => e.metric === "relevance_rationale");
  const busy = busyId === company.competitor_id;
  return (
    <div data-testid={`bench-suggested-${company.competitor_id}`} className="rounded-lg border border-amber-200 bg-amber-50/50 p-3">
      <div className="text-[13px] font-semibold text-[#0F1B2D]">{company.company_name}</div>
      {rationaleRow && <div className="mt-1 text-[11.5px] italic text-slate-500">"{rationaleRow.value_text}"</div>}
      <div className="mt-2 flex gap-2">
        <button
          type="button" data-testid={`bench-suggested-accept-${company.competitor_id}`} disabled={busy}
          onClick={() => onReview(company.competitor_id, "accept")}
          className="rounded-full border border-emerald-300 bg-white px-3 py-1 text-[11.5px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Accept
        </button>
        <button
          type="button" data-testid={`bench-suggested-ignore-${company.competitor_id}`} disabled={busy}
          onClick={() => onReview(company.competitor_id, "ignore")}
          className="rounded-full border border-slate-300 bg-white px-3 py-1 text-[11.5px] font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Ignore
        </button>
      </div>
    </div>
  );
}

function SuggestedCompetitorsSection({ snapshot, onReview, busyId }) {
  const suggested = snapshot?.suggested_competitors || [];
  if (!snapshot?.has_snapshot || suggested.length === 0) return null;
  return (
    <Section
      testid="bench-suggested-competitors"
      title="Suggested Direct Competitors — Needs Your Review"
      subtitle="Newly discovered direct competitors — excluded from your comparison and from Gap Intelligence until you accept or ignore them"
    >
      <div data-testid="bench-suggested-list" className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
        {suggested.map((c) => <SuggestedCard key={c.competitor_id} company={c} onReview={onReview} busyId={busyId} />)}
      </div>
    </Section>
  );
}

function CompetitiveLandscape({
  gmPct, cccDays, revPerEmp, companyName, customerId,
  snapshot, loadingSnap, refreshing, stage, error, cooldownS,
  addName, setAddName, addBusy, submitAddCompetitor, startRefresh,
}) {
  const hasSnapshot = !!snapshot?.has_snapshot;
  const hasAnyData = hasSnapshot
    || (snapshot?.competitors?.length > 0)
    || (snapshot?.leaders?.length > 0);

  return (
    <Section
      testid="bench-competitive-landscape"
      title="Competitive Landscape"
      subtitle="Your Company → Industry/Peer Benchmark → Comparable Competitors → Relevant Industry Leaders"
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div data-testid="bench-landscape-last-updated" className="text-[11px] text-slate-500">
          {hasSnapshot ? `Last updated ${new Date(snapshot.created_at).toLocaleString("en-IN")}` : "No comparison researched yet"}
        </div>
        <button
          type="button"
          data-testid="bench-refresh-btn"
          onClick={startRefresh}
          disabled={refreshing || cooldownS > 0 || !customerId}
          className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium text-[#0F1B2D] transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {refreshing
            ? (REFRESH_STAGE_LABELS[stage] || "Refreshing…")
            : cooldownS > 0
              ? `Refresh available in ${fmtCooldown(cooldownS)}`
              : "Refresh comparison"}
        </button>
      </div>

      {error && (
        <div data-testid="bench-refresh-error" className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] text-rose-700">
          {error}
        </div>
      )}

      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Your Company · Internal Actual</div>
        <div className="flex flex-wrap gap-4 text-[12.5px] text-[#0F1B2D]">
          <span>Gross Margin: {gmPct != null ? `${Number(gmPct).toFixed(1)}%` : "—"}</span>
          <span>CCC: {cccDays != null ? `${Math.round(Number(cccDays))} days` : "—"}</span>
          <span>Revenue / Employee: {revPerEmp != null ? (fmtShortInr(revPerEmp) || "—") : "—"}</span>
        </div>
      </div>

      {loadingSnap ? (
        <div className="text-[12px] text-slate-400">Loading…</div>
      ) : !hasAnyData ? (
        <div data-testid="bench-landscape-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
          <span className="text-[18px] leading-none text-slate-400">—</span>
          <span>
            No competitive research has been run yet. Click "Refresh comparison" to discover comparable companies
            and industry benchmarks — a live AI web-research pass (~1-2 min), never triggered automatically.
          </span>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Industry / Peer Benchmark</div>
            {(snapshot.industry_benchmark || []).length === 0 ? (
              <div data-testid="bench-industry-benchmark-empty" className="text-[11.5px] text-slate-400">
                No industry-level benchmark found this refresh.
              </div>
            ) : (
              <div data-testid="bench-industry-benchmark-list" className="rounded-lg border border-slate-200 bg-white p-3">
                {snapshot.industry_benchmark.map((e) => <EvidenceRow key={e.id} e={e} />)}
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Comparable Competitors</div>
            {(snapshot.competitors || []).length === 0 ? (
              <div data-testid="bench-competitors-empty" className="text-[11.5px] text-slate-400">
                No comparable competitors found yet — try Refresh again, or add one below.
              </div>
            ) : (
              <div className="space-y-2">
                {snapshot.competitors.map((c) => <CompanyCard key={c.competitor_id} company={c} testidPrefix="bench-competitor" />)}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <input
                data-testid="bench-add-competitor-input"
                value={addName}
                onChange={(ev) => setAddName(ev.target.value)}
                placeholder="Add a competitor by name…"
                className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-[12px] focus:border-slate-400 focus:outline-none"
              />
              <button
                type="button"
                data-testid="bench-add-competitor-btn"
                onClick={submitAddCompetitor}
                disabled={addBusy || !addName.trim()}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
              >
                Add
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Relevant Industry Leaders</div>
            {(snapshot.leaders || []).length === 0 ? (
              <div data-testid="bench-leaders-empty" className="text-[11.5px] text-slate-400">
                No category leaders found this refresh.
              </div>
            ) : (
              <div className="space-y-2">
                {snapshot.leaders.map((c) => <CompanyCard key={c.competitor_id} company={c} testidPrefix="bench-leader" />)}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 text-[10.5px] text-slate-400">
        External facts are AI-researched (Kimi web search) and carry whatever source the model found — "Sourced"
        facts name a publisher, "Unsourced" facts don't (and are never used for numeric comparison), "C-ORE
        inference" is our own reasoning (e.g. why a company is comparable), "Not found" means the data genuinely
        isn't available. Nothing here is independently fact-checked by {companyName || "C-ORE"}.
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Benchmarking Phase 3 (2026-02) — Gap Intelligence, Competitive
// Differentiators & Business Opportunity Intelligence.
//
// Reads `snapshot.intelligence` from the SAME zero-LLM read endpoint
// Competitive Landscape already uses — no separate fetch, no separate
// refresh trigger. Every badge/score/owner shown here was computed
// deterministically by the backend (benchmark_registries.py) AFTER the
// LLM's candidate was validated — never LLM-chosen. This section is
// intelligence, not a second recommendation engine: it never proposes an
// action, only an observation + evidence + a sized business opportunity.
// ---------------------------------------------------------------------------
const SEVERITY_BADGE = {
  critical:      { label: "Critical",      cls: "border-rose-300 bg-rose-50 text-rose-700" },
  high:          { label: "High",          cls: "border-amber-300 bg-amber-50 text-amber-700" },
  medium:        { label: "Medium",        cls: "border-sky-200 bg-sky-50 text-sky-700" },
  low:           { label: "Low",           cls: "border-slate-200 bg-slate-100 text-slate-500" },
  informational: { label: "Informational", cls: "border-slate-200 bg-slate-100 text-slate-500" },
};
const OWNER_BADGE = {
  ceo: "CEO", cfo: "CFO", cmo: "CMO", cto: "CTO", cross_functional: "Cross Functional",
};
const LIFECYCLE_BADGE = {
  new:       { label: "New",       cls: "text-sky-600" },
  recurring: { label: "Recurring", cls: "text-amber-600" },
  improving: { label: "Improving", cls: "text-emerald-600" },
  resolved:  { label: "Resolved",  cls: "text-slate-400" },
};
const DIRECTION_ARROW = { ahead: "↑ Ahead", behind: "↓ Behind", neutral: "≈ At par" };

function FindingMeta({ finding }) {
  const sev = SEVERITY_BADGE[finding.severity] || SEVERITY_BADGE.informational;
  const life = LIFECYCLE_BADGE[finding.lifecycle_state] || LIFECYCLE_BADGE.new;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 text-[10.5px]">
      <span className={`rounded-full border px-2 py-0.5 font-medium ${sev.cls}`}>{sev.label}</span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
        Owner: {OWNER_BADGE[finding.owner_seat] || finding.owner_seat}
      </span>
      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium capitalize text-slate-600">
        {(finding.business_area || "").replace(/_/g, " ")}
      </span>
      {finding.lifecycle_state && finding.lifecycle_state !== "new" && (
        <span className={`font-medium ${life.cls}`}>{life.label}</span>
      )}
      <span className="text-slate-400">Priority {finding.priority_score}</span>
    </div>
  );
}

function GapCard({ finding }) {
  return (
    <div data-testid={`bench-gap-${finding.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[13px] font-semibold text-[#0F1B2D]">{finding.title}</div>
        <span className="shrink-0 text-[11px] font-medium text-slate-500">
          {DIRECTION_ARROW[finding.gap_direction] || ""}
        </span>
      </div>
      <div className="mt-1 text-[12px] text-slate-600">{finding.observation}</div>
      <div className="mt-2 flex flex-wrap gap-4 rounded-md bg-slate-50 px-2.5 py-2 text-[11.5px] text-[#0F1B2D]">
        <span>You: {finding.internal_value != null ? `${finding.internal_value}${finding.unit ? ` ${finding.unit}` : ""}` : "—"}</span>
        <span>Benchmark: {finding.benchmark_value != null ? `${finding.benchmark_value}${finding.unit ? ` ${finding.unit}` : ""}` : "—"}</span>
        <span>Gap: {finding.gap_magnitude_pct != null ? `${finding.gap_magnitude_pct}%` : "—"}</span>
      </div>
      {finding.interpretation && <div className="mt-2 text-[11.5px] italic text-slate-500">{finding.interpretation}</div>}
      <FindingMeta finding={finding} />
    </div>
  );
}

function CreateTaskButton({ customerId, findingId, testid }) {
  const [state, setState] = useState("idle"); // idle | busy | done | dedup | error
  const [err, setErr] = useState(null);

  const click = async () => {
    setState("busy"); setErr(null);
    const res = await api.benchmarkCreateTaskFromFinding(customerId, findingId);
    if (res.ok) {
      setState(res.data.deduplicated ? "dedup" : "done");
    } else {
      setState("error"); setErr(res.error || "Could not create a task from this.");
    }
  };

  if (state === "done") return <span data-testid={testid} className="text-[11px] font-medium text-emerald-600">Added to Command Center</span>;
  if (state === "dedup") return <span data-testid={testid} className="text-[11px] font-medium text-slate-500">Already in Command Center</span>;
  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button" data-testid={testid} onClick={click} disabled={state === "busy"}
        className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[10.5px] font-medium hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {state === "busy" ? "Creating…" : "Create Command Center task"}
      </button>
      {err && <span className="text-[10.5px] text-rose-600">{err}</span>}
    </div>
  );
}

function DifferentiatorCard({ finding, customerId }) {
  return (
    <div data-testid={`bench-differentiator-${finding.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[13px] font-semibold text-[#0F1B2D]">{finding.title}</div>
      <div className="mt-1 text-[12px] text-slate-600">{finding.observation}</div>
      {finding.interpretation && <div className="mt-2 text-[11.5px] italic text-slate-500">{finding.interpretation}</div>}
      <FindingMeta finding={finding} />
      <div className="mt-2">
        <CreateTaskButton customerId={customerId} findingId={finding.id} testid={`bench-differentiator-create-task-${finding.id}`} />
      </div>
    </div>
  );
}

function OpportunityCard({ finding, customerId }) {
  return (
    <div data-testid={`bench-opportunity-${finding.id}`} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[13px] font-semibold text-[#0F1B2D]">{finding.title}</div>
      <div className="mt-1 text-[12px] text-slate-600">{finding.observation}</div>
      <div className="mt-2 rounded-md bg-slate-50 px-2.5 py-2 text-[12px] text-[#0F1B2D]">
        {finding.estimated_impact_value != null
          ? <span className="font-semibold">
              Estimated impact: {finding.estimated_impact_unit === "INR/year"
                ? `${fmtShortInr(finding.estimated_impact_value)}/year`
                : (fmtShortInr(finding.estimated_impact_value) || `${finding.estimated_impact_value} ${finding.estimated_impact_unit || ""}`)}
            </span>
          : <span className="text-slate-500">{finding.estimated_impact_text || "Qualitative opportunity — no defensible ₹ formula for this metric yet."}</span>}
        {finding.assumptions && <div className="mt-1 text-[10.5px] text-slate-400">Assumes: {finding.assumptions}</div>}
      </div>
      <FindingMeta finding={finding} />
      <div className="mt-2">
        <CreateTaskButton customerId={customerId} findingId={finding.id} testid={`bench-opportunity-create-task-${finding.id}`} />
      </div>
    </div>
  );
}

function GapIntelligenceSection({ snapshot, loadingSnap, companyName, customerId }) {
  const intel = snapshot?.intelligence || { gaps: [], differentiators: [], opportunities: [] };
  const hasAny = (intel.gaps?.length || 0) + (intel.differentiators?.length || 0) + (intel.opportunities?.length || 0) > 0;
  const hasSnapshot = !!snapshot?.has_snapshot;

  return (
    <Section
      testid="bench-gap-intelligence"
      title="Gap Intelligence, Differentiators & Opportunities"
      subtitle="Where you're ahead, where you're behind, why, and what business opportunity it implies"
    >
      {loadingSnap ? (
        <div className="text-[12px] text-slate-400">Loading…</div>
      ) : !hasSnapshot ? (
        <div data-testid="bench-gap-intelligence-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
          <span className="text-[18px] leading-none text-slate-400">—</span>
          <span>Run "Refresh comparison" above — this section is generated from the same snapshot as the Competitive Landscape.</span>
        </div>
      ) : !hasAny ? (
        <div data-testid="bench-gap-intelligence-not-enough" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
          <span className="text-[18px] leading-none text-slate-400">—</span>
          <span>
            Not enough comparable data yet. This snapshot's evidence didn't include a metric or capability we could
            validate against {companyName || "your"} real numbers — we never fabricate a comparison to fill this space.
          </span>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Gap Intelligence</div>
            {intel.gaps.length === 0 ? (
              <div data-testid="bench-gaps-empty" className="text-[11.5px] text-slate-400">Not enough comparable data.</div>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {intel.gaps.map((f) => <GapCard key={f.id} finding={f} />)}
              </div>
            )}
          </div>
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Competitive Differentiators</div>
            {intel.differentiators.length === 0 ? (
              <div data-testid="bench-differentiators-empty" className="text-[11.5px] text-slate-400">No evidenced differentiators this refresh.</div>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {intel.differentiators.map((f) => <DifferentiatorCard key={f.id} finding={f} customerId={customerId} />)}
              </div>
            )}
          </div>
          <div>
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Business Opportunities</div>
            {intel.opportunities.length === 0 ? (
              <div data-testid="bench-opportunities-empty" className="text-[11.5px] text-slate-400">No opportunity could be sized from this refresh's gaps/differentiators.</div>
            ) : (
              <div className="grid gap-2 md:grid-cols-2">
                {intel.opportunities.map((f) => <OpportunityCard key={f.id} finding={f} customerId={customerId} />)}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="mt-3 text-[10.5px] text-slate-400">
        These are business opportunities, not action items — Command Center, Company Trajectory and your CEO/CFO/CMO/CTO
        seats remain the execution and strategy layer. Every value shown is backend-validated against your real financial
        data and this snapshot's sourced evidence; anything that couldn't be validated was discarded, never estimated.
      </div>
    </Section>
  );
}


// ---------------------------------------------------------------------------
// SECTION 2 — Head-to-head (honest empty state until cohort exists)
//
// HARD RULE: Peer median + Top decile values can ONLY come from a real
// aggregated cohort of ≥10 customers, labeled with `n=` sample size.
// They are NEVER fabricated, LLM-generated, or back-filled from public
// data sources. Until the backend exposes a cohort endpoint, this
// section renders the unavailable state — exactly like the "3 biggest
// unlocks" and "AI strategic insights" sections do.
// ---------------------------------------------------------------------------
function HeadToHead({ companyName }) {
  const reasons = [
    "30 days of Tally data are required to compute your own metrics",
    "A peer cohort of ≥10 customers is required for peer-median + top-decile (we will never invent these)",
  ];
  return (
    <Section
      testid="bench-h2h"
      title="Head-to-head"
      subtitle="8 metrics · your values vs Peer Median vs Top Decile · unlocks after real data + real peer cohort"
    >
      <div
        data-testid="bench-h2h-empty"
        className="flex items-start gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-[12.5px] text-slate-600"
      >
        <span className="text-slate-400 text-[22px] leading-none">—</span>
        <div className="space-y-2">
          <div className="font-semibold text-[#0F1B2D]">
            Insufficient data — benchmarks unlock after Tally sync and once peer data is available
          </div>
          <ul className="list-disc pl-5 text-[11.5px] text-slate-500">
            {reasons.map((r) => (<li key={r}>{r}</li>))}
          </ul>
          <div className="text-[11px] text-slate-400">
            We will never show fabricated peer medians or fabricated ranks for {companyName || "your company"}.
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 3 — Unlock cards (already empty per earlier pass — kept honest)
// ---------------------------------------------------------------------------
function Unlocks() {
  return (
    <div data-testid="bench-unlocks-empty" className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500">
      <span className="text-slate-400 text-[18px] leading-none">—</span>
      <span>Unlock cards appear after a Tally sync, when peer comparison metrics can be computed. Smart Alerts already surface the most pressing financial gaps from your live data.</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION 4 — Data sources
//
// v1.5.0 (Phase 0 — Data Truth & Safety Cleanup, Feb 2026): the previous
// 13-item catalogue (RBI, SIAM, MCA, GSTN, EPFO, BSE/NSE, ICRA, CRISIL…)
// and the "14 public + private datasets" claim were decorative — none of
// those feeds are actually wired into this page's comparison logic.
// Listing them implied a data pipeline that does not exist, which
// contradicts this module's own "we will never show fabricated data"
// promise. A source is listed here ONLY once it is verifiably feeding
// live data into a customer's benchmark — never as a placeholder.
// ---------------------------------------------------------------------------
function DataSources() {
  return (
    <Section testid="bench-sources" title="Data sources powering the comparison" subtitle="No external datasets are wired in yet">
      <div
        data-testid="bench-sources-empty"
        className="flex items-start gap-2 rounded-md border border-dashed border-slate-200 bg-slate-50 px-3 py-6 text-[12.5px] text-slate-500"
      >
        <span className="text-slate-400 text-[18px] leading-none">—</span>
        <span>
          No external peer or industry dataset is connected to this comparison yet. A source will appear here only
          once it is verifiably feeding live data into your benchmark — never as a decorative placeholder.
        </span>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function Benchmarking() {
  const { customerData, dataAvailable, isLoading, companyName } = useBusinessProfile();

  // Benchmark Intelligence report state — lifted up here (not local to
  // CompetitiveLandscape) because Phase 3 made this explicit: "One
  // Benchmark Snapshot = One Intelligence Report." Competitive Landscape,
  // Gap Intelligence, Differentiators and Opportunities all read the SAME
  // snapshot and share the SAME refresh button/job — there is no separate
  // Phase 3 trigger, no second recommendation engine.
  const customer = getCurrentCustomer();
  const customerId = customer?.id || customer?.customer_id || null;

  const [snapshot, setSnapshot]       = useState(null);
  const [loadingSnap, setLoadingSnap] = useState(true);
  const [refreshing, setRefreshing]   = useState(false);
  const [stage, setStage]             = useState(null);
  const [error, setError]             = useState(null);
  const [cooldownS, setCooldownS]     = useState(0);
  const [addName, setAddName]         = useState("");
  const [addBusy, setAddBusy]         = useState(false);
  const [reviewBusyId, setReviewBusyId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const pollRef  = useRef(null);
  const jobIdRef = useRef(null);
  // Request-sequence guard (2026-02): if two overlapping GET
  // /competitive-snapshot calls are in flight (e.g. a fast re-render
  // firing the mount effect twice) and resolve out of order, a
  // slower/older response must never overwrite a newer one and pin the
  // page on a stale/empty state.
  const requestIdRef = useRef(0);

  const loadSnapshot = useCallback(async () => {
    if (!customerId) { setLoadingSnap(false); return; }
    const myRequestId = ++requestIdRef.current;
    const res = await api.benchmarkCompetitiveSnapshot(customerId, selectedProductId);
    if (myRequestId !== requestIdRef.current) return; // superseded by a newer call — discard
    if (res.ok) {
      setSnapshot(res.data);
      setCooldownS(res.data.cooldown_remaining_s || 0);
    }
    setLoadingSnap(false);
  }, [customerId, selectedProductId]);

  useEffect(() => { loadSnapshot(); }, [loadSnapshot]);
  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);
  useEffect(() => {
    const t = setInterval(() => setCooldownS((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, []);

  const pollJob = useCallback((jobId, startedAt) => {
    if (jobIdRef.current !== jobId) return;
    (async () => {
      const res = await api.benchmarkSnapshotJobStatus(jobId);
      if (jobIdRef.current !== jobId) return;
      const timedOut = Date.now() - startedAt > BENCH_REFRESH_TIMEOUT_MS;
      if (!res.ok) {
        if (timedOut) {
          setError("Refresh is taking longer than expected. Please try again shortly.");
          setStage(null); setRefreshing(false);
          return;
        }
        pollRef.current = setTimeout(() => pollJob(jobId, startedAt), BENCH_REFRESH_POLL_MS);
        return;
      }
      const j = res.data;
      if (j.status === "completed") {
        setStage(null); setRefreshing(false);
        loadSnapshot();
        return;
      }
      if (j.status === "failed") {
        setError(j.failure_reason || "Could not refresh the competitive landscape. Please try again.");
        setStage(null); setRefreshing(false);
        loadSnapshot();
        return;
      }
      if (timedOut) {
        setError("Refresh is taking longer than expected. Please check back shortly.");
        setStage(null); setRefreshing(false);
        return;
      }
      setStage(j.stage || j.status);
      pollRef.current = setTimeout(() => pollJob(jobId, startedAt), BENCH_REFRESH_POLL_MS);
    })();
  }, [loadSnapshot]);

  const startRefresh = useCallback(async () => {
    if (!customerId) return;
    setError(null);
    setRefreshing(true);
    setStage("queued");
    const res = await api.benchmarkRefreshSnapshot(customerId, selectedProductId);
    if (!res.ok) {
      setRefreshing(false);
      setStage(null);
      if (res.cooldown) {
        setCooldownS(res.retryAfterS || 0);
        setError(res.error || "Please wait before refreshing again.");
      } else {
        setError(res.error || "Could not start the refresh.");
      }
      return;
    }
    jobIdRef.current = res.data.job_id;
    pollJob(res.data.job_id, Date.now());
  }, [customerId, selectedProductId, pollJob]);

  const submitAddCompetitor = useCallback(async () => {
    const name = addName.trim();
    if (!name || !customerId) return;
    setAddBusy(true);
    const res = await api.benchmarkAddCompetitor(customerId, name);
    setAddBusy(false);
    if (res.ok) { setAddName(""); loadSnapshot(); }
    else setError(res.error || "Could not add this competitor.");
  }, [addName, customerId, loadSnapshot]);

  const handleReviewCompetitor = useCallback(async (competitorId, decision) => {
    setReviewBusyId(competitorId);
    const res = await api.benchmarkReviewCompetitor(competitorId, decision);
    setReviewBusyId(null);
    if (res.ok) loadSnapshot();
    else setError(res.error || "Could not update this competitor.");
  }, [loadSnapshot]);

  // v1.4.5 — header label MUST reflect the customer's real region or
  // omit a cohort name entirely. The previous hardcoded "Pune MIDC
  // network" mis-located a Nashik customer, directly contradicting
  // this page's own "We will never show fabricated peer medians"
  // promise. Read city/state from the dashboard payload (sourced from
  // `customers.city/state`, captured at signup). A real peer cohort
  // lights up only when ≥10 customers in the same region have synced
  // — until then we ship the honest "peer cohort pending" label.
  const customerCity   = (customerData?.customer_city  || "").trim();
  const customerState  = (customerData?.customer_state || "").trim();
  const cohortLabel = customerCity
    ? `peer cohort · ${customerCity}${customerState ? `, ${customerState}` : ""} (pending ≥10 peers)`
    : "peer cohort pending";

  return (
    <div data-testid="bench-module">
      <ConnectTallyBanner dataAvailable={dataAvailable} isLoading={isLoading} />
      <div className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
        {/* v1.5.2 (Feb 2026, Phase 1) — the "Anonymised peer set" badge was
            removed: no genuine anonymised peer cohort exists yet (the title
            below already honestly says "peer cohort pending"); showing both
            side-by-side implied live peer data that isn't there. */}
        <ModuleHeader
          overline="Benchmarking & Industry Intelligence"
          title={`How ${companyName || "your company"} ranks · ${cohortLabel}`}
        />

        <section data-testid="bench-kpis" className="grid grid-cols-1 gap-3 max-w-[260px]">
          {KPIS.map((k) => (
            <KpiCard
              key={k.id}
              kpi={k}
              testidPrefix="bench-kpi"
              isLoading={isLoading}
              dataAvailable={dataAvailable}
              realValue={realValueForBench(k.realKey, customerData)}
              forceSub
            />
          ))}
        </section>

        <BenchmarkProductsSection
          customerId={customerId}
          selectedProductId={selectedProductId}
          onSelectProduct={setSelectedProductId}
          onProductsChanged={loadSnapshot}
        />

        <CompetitiveLandscape
          gmPct={realValueForBench("gmPct", customerData)}
          cccDays={realValueForBench("cccDays", customerData)}
          revPerEmp={realValueForBench("revPerEmp", customerData)}
          companyName={companyName}
          customerId={customerId}
          snapshot={snapshot}
          loadingSnap={loadingSnap}
          refreshing={refreshing}
          stage={stage}
          error={error}
          cooldownS={cooldownS}
          addName={addName}
          setAddName={setAddName}
          addBusy={addBusy}
          submitAddCompetitor={submitAddCompetitor}
          startRefresh={startRefresh}
        />

        <SuggestedCompetitorsSection
          snapshot={snapshot}
          onReview={handleReviewCompetitor}
          busyId={reviewBusyId}
        />

        <GapIntelligenceSection snapshot={snapshot} loadingSnap={loadingSnap} companyName={companyName} customerId={customerId} />

        <HeadToHead companyName={companyName} />

        <Section testid="bench-unlocks-section" title="3 biggest unlocks" subtitle="Tied directly to head-to-head gaps">
          <Unlocks />
        </Section>

        <InsightsGrid
          subtitle="What your Benchmarking advisor would tell you today"
          fetchFn={() => api.aiInsightsGenerate("intelligence", api.benchmarkInsights)}
          testidPrefix="bench"
          dataAvailable={dataAvailable}
        />

        <DataSources />

        <ChatPanel
          title="Ask your Benchmarking advisor"
          subtitle="Peer rank, unlocks, gaps · Hindi or English"
          placeholder="Ask about CCC, GM or NPS rank vs peers…"
          sendFn={api.benchmarkChat}
          testidPrefix="bench"
          emptyHint="Ask how you stack up. Try: 'What's my biggest gap to peers?' or 'Which metric can I unlock fastest?'"
        />
      </div>
    </div>
  );
}
